/**
 * Full-screen wellness check-in confirm — presented when the recipient TAPS a
 * wellness check-in push (the lock-screen "I'm OK" action stays silent; this
 * is the in-app equivalent with an actual moment of feedback).
 *
 * One giant circle: red "I'm OK" → tap → springs to green with a check while
 * the ack posts (/wellness/im-ok — same endpoint the notification action and
 * the watch use; it cancels any pending escalation ladder server-side), a
 * success haptic fires, and the sheet dismisses itself. Failure rolls back to
 * red with a retry note — the recipient must never believe they checked in
 * when the server didn't hear it.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing, Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { apiPost } from '../utilities/apiClient';
import { hapticCreationPulse } from '../utilities/haptics';
import Icon from './Icon';
import debug from '../utilities/debug';
import {
  PendingWellnessCheckIn,
  subscribeWellnessCheckIn,
  clearWellnessCheckIn,
} from '../utilities/wellnessCheckInStore';

const RED = '#EF4444';
const GREEN = '#10B981';
const DISMISS_AFTER_MS = 1400;

export default function WellnessCheckInSheet(): React.JSX.Element | null {
  const theme = useTheme();
  const [pending, setPending] = useState<PendingWellnessCheckIn | null>(null);
  const [phase, setPhase] = useState<'idle' | 'confirming' | 'done' | 'error'>('idle');

  // 0 → red idle, 1 → green confirmed. Colors interpolate on the JS driver;
  // the tick gets its own native-driver spring.
  const progress = useRef(new Animated.Value(0)).current;
  const tickScale = useRef(new Animated.Value(0)).current;
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // subscribe replays any already-pending check-in (push tapped pre-login)
    // and returns the unsubscribe, which this effect explicitly hands back so
    // an unmounted sheet can never receive a late setState.
    const unsubscribe = subscribeWellnessCheckIn((p) => {
      setPending(p);
      if (p) {
        setPhase('idle');
        progress.setValue(0);
        tickScale.setValue(0);
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
  }, []);

  const confirm = useCallback(async () => {
    if (!pending || phase === 'confirming' || phase === 'done') return;
    setPhase('confirming');

    try {
      await apiPost(`/wellness/im-ok/${pending.projectId}/${pending.accountId}/${pending.cognitoId}`, {});

      Animated.timing(progress, {
        toValue: 1,
        duration: 420,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
      Animated.spring(tickScale, {
        toValue: 1,
        friction: 4,
        tension: 90,
        delay: 180,
        useNativeDriver: true,
      }).start();

      hapticCreationPulse();
      setPhase('done');
      dismissTimer.current = setTimeout(() => clearWellnessCheckIn(), DISMISS_AFTER_MS);
    } catch (err: any) {
      debug.warn('[wellness-checkin] im-ok failed', { err: err?.message });
      // Roll back — a failed ack must not look like a successful one.
      Animated.timing(progress, { toValue: 0, duration: 220, useNativeDriver: false }).start();
      Animated.timing(tickScale, { toValue: 0, duration: 150, useNativeDriver: true }).start();
      setPhase('error');
    }
  }, [pending, phase, progress, tickScale]);

  const dismiss = useCallback(() => {
    if (phase === 'confirming') return;
    clearWellnessCheckIn();
  }, [phase]);

  if (!pending) return null;

  const circleColor = progress.interpolate({ inputRange: [0, 1], outputRange: [RED, GREEN] });
  const haloColor = progress.interpolate({
    inputRange: [0, 1],
    outputRange: ['rgba(239,68,68,0.18)', 'rgba(16,185,129,0.18)'],
  });

  return (
    <Modal visible transparent animationType="fade" statusBarTranslucent onRequestClose={dismiss}>
      <View style={[styles.overlay, { backgroundColor: theme.dark ? 'rgba(6,10,18,0.96)' : 'rgba(255,255,255,0.97)' }]}>
        <Text style={[styles.title, { color: theme.colors.text }]}>Are you OK?</Text>
        <Text style={[styles.subtitle, { color: theme.colors.muted }]}>
          Your care team asked to check in. One tap lets them know you’re alright.
        </Text>

        <TouchableOpacity activeOpacity={0.85} onPress={confirm} disabled={phase === 'done'}>
          <Animated.View style={[styles.halo, { backgroundColor: haloColor }]}>
            <Animated.View style={[styles.circle, { backgroundColor: circleColor }]}>
              {phase === 'done' || phase === 'confirming' ? (
                <Animated.View style={{ transform: [{ scale: tickScale }] }}>
                  <Icon icon={['fas', 'check']} size={64} color="#fff" />
                </Animated.View>
              ) : (
                <Text style={styles.circleText}>I’m OK</Text>
              )}
            </Animated.View>
          </Animated.View>
        </TouchableOpacity>

        <Text style={[styles.status, { color: phase === 'error' ? RED : theme.colors.muted }]}>
          {phase === 'done'
            ? 'Thanks — your care team has been told.'
            : phase === 'error'
              ? 'That didn’t go through — tap to try again.'
              : ' '}
        </Text>

        {phase !== 'done' && phase !== 'confirming' && (
          <TouchableOpacity onPress={dismiss} hitSlop={{ top: 10, bottom: 10, left: 20, right: 20 }}>
            <Text style={[styles.notNow, { color: theme.colors.muted }]}>Not now</Text>
          </TouchableOpacity>
        )}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 32 },
  title: { fontSize: 30, fontWeight: '800', marginBottom: 10, textAlign: 'center' },
  subtitle: { fontSize: 15, lineHeight: 21, textAlign: 'center', marginBottom: 40, maxWidth: 320 },
  halo: {
    width: 216, height: 216, borderRadius: 108,
    alignItems: 'center', justifyContent: 'center',
  },
  circle: {
    width: 176, height: 176, borderRadius: 88,
    alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.18, shadowRadius: 16,
    elevation: 8,
  },
  circleText: { color: '#fff', fontSize: 26, fontWeight: '800' },
  status: { fontSize: 14, marginTop: 28, textAlign: 'center', minHeight: 20 },
  notNow: { fontSize: 15, fontWeight: '600', marginTop: 18, padding: 6 },
});
